import { ColDef, ColDefField, ColGroupDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import { useAtomValue } from 'jotai';
import { capitalize, get } from 'lodash-es';
import { useMemo } from 'react';
import * as yup from 'yup';
import {
  ICaptableDataModel,
  ICaptableInvestmentDataModel,
} from '../../../../data-models/captable2.data-model';
import {
  createIntegerField,
  createPercentField,
  field3ToColumnDef,
  IField3,
  INumberMeta,
} from '../../../../data-models/field3.data-model';
import { createUSDFormatterDataModel } from '../../../../data-models/formatter.data-model';
import { CaptableSummaryColumnType } from '../../../../data-models/preferences-v3.data-model';
import { ViewModelInvestmentType } from '../../../../schemas/common-schema-defs';
import { getExcelFormatterId } from '../../../../util/excel-formatter-service';
import { FMT } from '../../../../util/formatter-service';
import { useSchemaToColDef } from '../../../../util/schema-utils';
import { captureUnexpectedState } from '../../../../util/SentryUtils';
import { getShareClassType } from '../../../../view-models/captable.view-model';
import { numericCellClasses } from '../../../Finance2/common-grid-defs/commonColDefs';
import { ISortableCaptableInvestment, sortShareClasses } from '../investment-utils';
import {
  captableSummaryPreferencesAtom,
  DefaultCaptableInvestmentColumnOrder,
} from './CaptableSummaryPreferences';
import { ICaptableSummaryRowData } from './CaptableSummaryRowData';

const stakeholderSchema = yup.string().required().label('Stakeholder');

type CaptableSummaryDefaultColDefs<TData = ICaptableSummaryRowData> = ColDef<TData> & {
  ctColumnType?: CaptableSummaryColumnType;
};

function getOrderMap(keys: CaptableSummaryColumnType[]) {
  return new Map<CaptableSummaryColumnType, number>(keys.map((k, i) => [k, i]));
}

export function useCaptableSummaryColDefs(ct: ICaptableDataModel | null) {
  const schemaFieldToColDef = useSchemaToColDef();
  const prefs = useAtomValue(captableSummaryPreferencesAtom);
  const { pinTotalColumns = false, investmentColumnOrder = DefaultCaptableInvestmentColumnOrder } = prefs;
  const columnOrder = useMemo(() => getOrderMap(investmentColumnOrder), [investmentColumnOrder]);
  const colDefs: (ColDef<ICaptableSummaryRowData> | ColGroupDef<ICaptableSummaryRowData>)[] = useMemo(() => {
    if (!ct) {
      return [];
    }

    const defs: (ColDef<ICaptableSummaryRowData> | ColGroupDef<ICaptableSummaryRowData>)[] = [
      {
        ...schemaFieldToColDef(
          'stakeholderName' as keyof ICaptableSummaryRowData,
          stakeholderSchema.describe()
        ),
        valueFormatter: (params) => {
          if (params.node?.footer) {
            return 'Total';
          }
          return params.value;
        },
        minWidth: 200,
        pinned: 'left',
        getQuickFilterText: (params) => {
          return params.value;
        },
      },
    ];

    const sortedInvestments = sortCaptableInvestments(ct.captableInvestments ?? []);

    const typeGroups = new Map<ViewModelInvestmentType, ICaptableInvestmentDataModel[]>();
    sortedInvestments?.forEach((investment) => {
      if (!investment.investmentType) {
        return;
      }
      const type = getShareClassType(investment.investmentType!, investment.shareClassType);
      if (!type) {
        return;
      }
      const group = typeGroups.get(type) ?? [];
      group.push(investment);
      typeGroups.set(type, group);
    });

    typeGroups.forEach((investments, type) => {
      defs.push({
        headerName: capitalize(type!),
        headerClass: 'type-header',
        children: investments.map((investment) => {
          const fields = getInvestmentColumnDefs({ investment, type: type!, columnOrder });

          return {
            headerName: investment.name,
            headerClass: 'investment-header',
            children: fields,
          };
        }),
      });
    });
    defs.push(getTotalColumnDefs({ columnOrder, pinTotalColumns }));

    return defs;
  }, [columnOrder, ct, pinTotalColumns, schemaFieldToColDef]);

  return colDefs;
}

export function getInvestmentColumns(investmentType: ViewModelInvestmentType) {
  switch (investmentType) {
    case ViewModelInvestmentType.preferred:
    case ViewModelInvestmentType.warrants:
    case ViewModelInvestmentType.common: {
      return new Set(['cashRaised', 'fullyDilutedShares', 'ownershipPercentage']);
    }
    case ViewModelInvestmentType.notes: {
      return new Set(['principal']);
    }

    case ViewModelInvestmentType.options: {
      return new Set(['optionsSharesHeld', 'ownershipPercentage']);
    }
    case ViewModelInvestmentType.cash: {
      return new Set(['cashRaised']);
    }
    case ViewModelInvestmentType.debt: {
      return new Set(['cashRaised']);
    }
    default:
      return new Set();
  }
}

function getInvestmentKey(inv: ICaptableInvestmentDataModel) {
  return `${inv.name}-${getShareClassType(inv.investmentType, inv.shareClassType)}`;
}

function sortCaptableInvestments(
  captableInvestments: ICaptableInvestmentDataModel[]
): ICaptableInvestmentDataModel[] {
  const investmentsByNameAndType = new Map<string, ICaptableInvestmentDataModel>(
    captableInvestments?.map((i) => [getInvestmentKey(i), i]) ?? []
  );
  const sorted = sortShareClasses(
    captableInvestments.reduce(
      (acc, inv) => {
        const sortable = captableInvestmentToSortable(inv);
        if (!sortable) {
          return acc;
        }
        acc.push(sortable);
        return acc;
      },
      [] as (ICaptableInvestmentDataModel & ISortableCaptableInvestment)[]
    )
  );
  return sorted.map((inv) => investmentsByNameAndType.get(getInvestmentKey(inv)) ?? inv);
}

export function captableInvestmentToSortable(
  inv: ICaptableInvestmentDataModel
): (ICaptableInvestmentDataModel & ISortableCaptableInvestment) | null {
  const type = getShareClassType(inv.investmentType, inv.shareClassType);
  if (!type) {
    captureUnexpectedState('Missing share class type', { inv });
    return null;
  }
  return {
    ...inv,
    type,
    investmentDate: inv.investmentDate ? new Date(inv.investmentDate) : null,
  };
}

function getInvestedAmountField(
  colId: string,
  type: ViewModelInvestmentType | null
): CaptableSummaryDefaultColDefs {
  const formatterDataModel = createUSDFormatterDataModel(0);
  const formatter = FMT.get().getFormatterForModel(formatterDataModel);

  return {
    cellClass: [...numericCellClasses, getExcelFormatterId('currency')],
    ctColumnType: CaptableSummaryColumnType.amount,
    valueFormatter: (params: ValueFormatterParams) => {
      if (params.value == null) {
        return '';
      }

      return formatter(params.value);
    },
    valueGetter: (params: ValueGetterParams) => {
      return get(params.data, colId);
    },
    colId,
    field: colId as ColDefField,
    headerName:
      // ideally this should come from the schema field label, but we first need to refactor all of those so we have a separate field getter function
      type === ViewModelInvestmentType.cash || type === ViewModelInvestmentType.debt
        ? 'Amount'
        : 'Invested Amount',
    aggFunc: 'sum',
  } as CaptableSummaryDefaultColDefs<ICaptableSummaryRowData>;
}

function getSharesHeldField(
  overrides: Partial<IField3<INumberMeta>> = {}
): CaptableSummaryDefaultColDefs<ICaptableSummaryRowData> {
  return {
    ...field3ToColumnDef(
      createIntegerField({
        displayName: 'Shares Held',
        ...overrides,
      })
    ),
    ctColumnType: CaptableSummaryColumnType.noOfShares,
    aggFunc: 'sum',
  } as CaptableSummaryDefaultColDefs<ICaptableSummaryRowData>;
}

function getOwnershipPercentageField(
  overrides: Partial<IField3<INumberMeta>> = {}
): CaptableSummaryDefaultColDefs<ICaptableSummaryRowData> {
  return {
    ...field3ToColumnDef(
      createPercentField({
        displayName: 'Ownership Percentage',
        ...overrides,
      })
    ),
    ctColumnType: CaptableSummaryColumnType.ownershipPercentage,
    aggFunc: 'sum',
  } as CaptableSummaryDefaultColDefs<ICaptableSummaryRowData>;
}

export const CaptableSummaryDefaultColDefs: ColDef<ICaptableSummaryRowData> = {
  flex: 1,
  minWidth: 140,
  getQuickFilterText: () => '',
};

function getTotalColumnDefs({
  columnOrder,
  pinTotalColumns,
}: {
  columnOrder: Map<CaptableSummaryColumnType | '', number>;
  pinTotalColumns: boolean;
}): ColGroupDef<ICaptableSummaryRowData> {
  const pinned = pinTotalColumns ? ('right' as const) : false;
  return {
    headerName: '',
    headerClass: 'type-header',
    children: [
      {
        headerName: 'Total',
        headerClass: 'type-header total-header',
        children: [
          {
            ...getInvestedAmountField('totals.cashRaised', null),
            pinned,
          },
          {
            ...getSharesHeldField({
              entityKey: 'totals.fullyDilutedShares',
            }),
            pinned,
          },
          {
            ...getOwnershipPercentageField({
              entityKey: 'totals.ownershipPercentage',
            }),
            pinned,
          },
        ].sort(
          (a, b) =>
            (columnOrder.get(a.ctColumnType ?? '') ?? Number.MAX_SAFE_INTEGER) -
            (columnOrder.get(b.ctColumnType ?? '') ?? Number.MAX_SAFE_INTEGER)
        ),
      },
    ],
  };
}

function getInvestmentColumnDefs({
  investment,
  type,
  columnOrder,
}: {
  investment: ICaptableInvestmentDataModel;
  type: ViewModelInvestmentType;
  columnOrder: Map<CaptableSummaryColumnType | '', number>;
}) {
  const columns = getInvestmentColumns(type);
  const fields = [];
  if (columns.has('cashRaised')) {
    fields.push({
      ...getInvestedAmountField(`investments.[${type}].[${investment.name}].cashRaised`, type),
    });
  }
  if (columns.has('fullyDilutedShares')) {
    fields.push({
      ...getSharesHeldField({
        entityKey: `investments.[${type}].[${investment.name}].fullyDilutedShares`,
      }),
    });
  }
  if (columns.has('ownershipPercentage')) {
    fields.push({
      ...getOwnershipPercentageField({
        entityKey: `investments.[${type}].[${investment.name}].ownershipPercentage`,
      }),
    });
  }
  if (columns.has('principal')) {
    fields.push({
      ...getInvestedAmountField(`investments.[${type}].[${investment.name}].cashRaised`, type),
    });
  }
  if (columns.has('optionsSharesHeld')) {
    fields.unshift({
      ...getSharesHeldField({
        entityKey: '',
      }),
      valueGetter: (params: ValueGetterParams<ICaptableSummaryRowData>) => {
        const { data } = params;
        if (!data) {
          return;
        }
        return (
          (data.investments?.[type]?.[investment.name]?.outstandingEquityAwardDerivatives ?? 0) +
          (data.investments?.[type]?.[investment.name]?.outstandingCommittedRestrictedStockAwards ?? 0)
        );
      },
    });
  }

  return fields.sort(
    (a, b) =>
      (columnOrder.get(a.ctColumnType ?? '') ?? Number.MAX_SAFE_INTEGER) -
      (columnOrder.get(b.ctColumnType ?? '') ?? Number.MAX_SAFE_INTEGER)
  );
}
